import type { SpreadGroup, ClassifiedOption } from "@/lib/classify-positions";
import { optionTradeFees, type FeeSchedule } from "@/lib/fees";
import type { ScannedOption, SpreadResult } from "../scan-table";
import type { VseLiveData } from "../types";
import { contractsForSpread, type PositionSizes } from "./config";
import { type RatingBreakdown } from "./ratings";

/** Placeholder rating breakdown for backend-supplied spreads. The Trading
 *  Opportunities table renders only the composite (carried as spreadComposite),
 *  not the per-dimension breakdown, so these zeros are never displayed. */
export const ZERO_BREAKDOWN: RatingBreakdown = {
  dteRaw: 0, dteWeighted: 0, baSpreadPctRaw: 0, baSpreadPctWeighted: 0,
  probTouchRaw: 0, probTouchWeighted: 0, hvPctRaw: 0, hvPctWeighted: 0,
  ivHvRatioRaw: 0, ivHvRatioWeighted: 0, minPitmRaw: 0, minPitmWeighted: 0, optionComposite: 0,
  dtePass: true, oiPass: true, rejected: false, composite: 0, passes: true,
};

/**
 * "Buy more" for a held spread: an opening order for the same two legs at the
 * current credit, sized by this strategy's settings. Legs come from the scan
 * when it covers them, else from the spread's live quote.
 */
export function buyMoreSpread(
  sp: SpreadGroup | null,
  isPut: boolean,
  ctx: {
    scanLeg?: (optionSymbol: string) => ScannedOption | undefined;
    live: VseLiveData | null;
    cfg: Record<string, string>;
    positionSizes: PositionSizes;
    feeSchedule: FeeSchedule;
  },
): SpreadResult | null {
  if (!sp) return null;
  let shortLeg = ctx.scanLeg?.(sp.shortLeg.optionSymbol.replace(/\s+/g, ""));
  let longLeg = ctx.scanLeg?.(sp.longLeg.optionSymbol.replace(/\s+/g, ""));
  if (!shortLeg || !longLeg) {
    // Held legs aren't in the current scan — rebuild them from the live
    // per-position quote so the price reflects the same B/A/M shown on the
    // row. dte comes from the held expiration: the order modal derives the
    // order's expiration from shortLeg.dte, so it must be accurate.
    const live = ctx.live;
    if (!live) return null;
    const dte = Math.max(0, Math.round((new Date(sp.expiration + "T00:00:00").getTime() - Date.now()) / 86400000));
    const legFrom = (
      held: ClassifiedOption, bid: number, ask: number,
      iv: number | null, openInterest: number | null, probTouch: number | null,
    ): ScannedOption => ({
      symbol: sp.underlying, optionSymbol: held.optionSymbol, strike: held.strike, dte,
      bid, ask, iv, lastPrice: live.underlyingPrice, optionLast: 0, premiumPct: 0,
      probTouch, eProbTouch: null, pProbItm: null, openInterest: openInterest ?? 0,
      description: held.description, marketCap: live.marketCap,
      avgDailyVolume: live.avgDailyVolume, hvPct: live.currentHvPct ?? 0,
      hvDte: live.currentHvDte ?? 0, optionType: isPut ? "put" : "call",
    });
    shortLeg = shortLeg ?? legFrom(sp.shortLeg, live.shortBid, live.shortAsk, live.shortIv, live.shortOpenInterest, live.shortProbItm);
    longLeg = longLeg ?? legFrom(sp.longLeg, live.longBid, live.longAsk, live.longIv, null, null);
  }
  if (!shortLeg || !longLeg) return null;
  const mid = (o: ScannedOption) => (o.bid + o.ask) / 2;
  const netCredit = Math.max(0.01, Math.round((mid(shortLeg) - mid(longLeg)) * 100) / 100);
  const spreadWidth = Math.abs(shortLeg.strike - longLeg.strike);
  const maxLoss = Math.max(0, spreadWidth - netCredit);
  const contracts = contractsForSpread(ctx.cfg, ctx.positionSizes, isPut ? "bull_put" : "bear_call", maxLoss);
  const openFees = optionTradeFees(ctx.feeSchedule, shortLeg.symbol, contracts, 2, 1);
  const fees = optionTradeFees(ctx.feeSchedule, shortLeg.symbol, contracts, 2, 2);
  return {
    shortLeg, longLeg, spreadType: isPut ? "bull_put" : "bear_call",
    netCredit, spreadWidth, maxLoss, maxGain: netCredit,
    contracts,
    rorAnnualPct: 0, rorAnnualPctLessNickel: 0, shortRating: ZERO_BREAKDOWN,
    rorRaw: 0, rorWeighted: 0, netCreditRaw: 0, netCreditWeighted: 0,
    eProbTouchRaw: 0, eProbTouchWeighted: 0, pProbItmRaw: 0, pProbItmWeighted: 0,
    otmRaw: 0, otmWeighted: 0, absReturnRaw: 0, absReturnWeighted: 0, maxWidthRaw: 0, maxWidthWeighted: 0,
    spreadComposite: 0,
    fees, openFees,
  };
}
